import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { apiClient } from '@/api/client'
import { useTeam } from '@/hooks/useTeam'
import { useTeamUsers } from '@/hooks/useTeamUsers'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { getTeamThatScoredPoints } from '@/utils/goalUtils'
import { Trash2, AlertTriangle, Trophy, Target, User, RefreshCw } from 'lucide-react'
import type { GameType, Team, Role } from '@foosball/shared'

interface DetailedGoalEntryProps {
  gameType: GameType
  targetScore: number
  gameMode: 'team' | 'cross_team' | 'free_play'
}

type ShooterPosition = 'attack' | 'midfield' | 'defense' | 'goalkeeper'
type EventType = 'shot' | 'monsterblock' | 'gamelle' | 'own_goal'

interface GoalRecord {
  id: string
  scoringTeam: Team
  shooterPosition: ShooterPosition
  eventType: EventType
  wasTouched: boolean
  points: number
  monsterblockTarget?: 'defense' | 'goalkeeper'
  timestamp: Date
}

interface PositionSwap {
  id: string
  team: Team
  timestamp: Date
  // Record what the positions were AFTER the swap
  defensePlayer: string
  attackPlayer: string
}

export function DetailedGoalEntryV2({ gameType, targetScore, gameMode }: DetailedGoalEntryProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { getAccessTokenSilently } = useAuth0()
  const { currentTeam } = useTeam()
  const { data: users, isLoading: usersLoading } = useTeamUsers(currentTeam?.id)

  const [participants, setParticipants] = useState<{
    redDefense?: string
    redAttack?: string
    blueDefense?: string
    blueAttack?: string
  }>({})

  const [goals, setGoals] = useState<GoalRecord[]>([])
  const [positionSwaps, setPositionSwaps] = useState<PositionSwap[]>([])
  
  const [currentGoal, setCurrentGoal] = useState<{
    scoringTeam: Team
    shooterPosition: ShooterPosition
    eventType: EventType
    wasTouched: boolean
    monsterblockTarget?: 'defense' | 'goalkeeper'  // For monsterblock: which position was blocked
  }>({
    scoringTeam: 'red',
    shooterPosition: 'attack',
    eventType: 'shot',
    wasTouched: false,
    monsterblockTarget: undefined,
  })

  const [gameEnded, setGameEnded] = useState(false)
  const [instantLoss, setInstantLoss] = useState<{ team: Team; reason: string } | null>(null)

  // Get team players info for display
  const getTeamPlayers = (team: Team) => {
    if (gameType === '1v1') {
      const playerId = team === 'red' ? participants.redDefense : participants.blueDefense
      const player = users?.find((u: any) => u.id === playerId)
      return player ? [player] : []
    } else {
      const defenseId = team === 'red' ? participants.redDefense : participants.blueDefense
      const attackId = team === 'red' ? participants.redAttack : participants.blueAttack
      const defense = users?.find((u: any) => u.id === defenseId)
      const attack = users?.find((u: any) => u.id === attackId)
      return [defense, attack].filter(Boolean) as any[]
    }
  }

  const createGame = useMutation({
    mutationFn: async (gameData: any) => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          scope: 'openid profile email',
        },
      })
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.post('/games', gameData)
      return response.data
    },
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['games'] })
      queryClient.invalidateQueries({ queryKey: ['team-stats'] })
      queryClient.invalidateQueries({ queryKey: ['player-stats'] })
      queryClient.invalidateQueries({ queryKey: ['global-leaderboard'] })
      queryClient.invalidateQueries({ queryKey: ['global-team-stats'] })
      navigate('/games')
    },
  })

  // Company Controlling Rules Engine
  const calculatePoints = (
    scoringTeam: Team,
    shooterPosition: ShooterPosition,
    eventType: EventType,
    wasTouched: boolean
  ): { shooterTeamDelta: number; opponentTeamDelta: number; instantLoss: boolean; losingTeam?: Team } => {
    
    // Own Goal Rules
    if (eventType === 'own_goal') {
      if (shooterPosition === 'attack') {
        // Own goal from attack
        return wasTouched 
          ? { shooterTeamDelta: 0, opponentTeamDelta: 1, instantLoss: false }
          : { shooterTeamDelta: 2, opponentTeamDelta: 0, instantLoss: false }
      } else {
        // Own goal from midfield, defense, or goalkeeper
        return wasTouched
          ? { shooterTeamDelta: 0, opponentTeamDelta: 1, instantLoss: false }
          : { shooterTeamDelta: 0, opponentTeamDelta: 0, instantLoss: true, losingTeam: scoringTeam }
      }
    }

    // Gamelle Rules (opponent gets negative points)
    if (eventType === 'gamelle') {
      const basePoints = {
        'attack': -2,
        'midfield': -1,  // Corrected: Gamelle from midfield is -1 for opponent
        'defense': -3,
        'goalkeeper': -5
      }[shooterPosition]
      
      return wasTouched
        ? { shooterTeamDelta: 0, opponentTeamDelta: -1, instantLoss: false }
        : { shooterTeamDelta: 0, opponentTeamDelta: basePoints, instantLoss: false }
    }

    // Monsterblock Rules (only from attack position)
    // scoringTeam = team that performed the BLOCK (gets the points)
    // shooterPosition is always 'attack' (the blocker)
    // We need to know what position was blocked (passed separately)
    if (eventType === 'monsterblock') {
      // Points go to the BLOCKING team (scoringTeam)
      // We don't have monsterblockTarget here, so return 0 for now
      // This will be calculated in addGoal with the actual target
      return { shooterTeamDelta: 0, opponentTeamDelta: 0, instantLoss: false }
    }

    // Normal Shot Rules
    if (eventType === 'shot') {
      const pointsMap = {
        'attack': wasTouched ? 1 : 2,
        'midfield': wasTouched ? 1 : -1,
        'defense': wasTouched ? 1 : 3,
        'goalkeeper': wasTouched ? 1 : 5
      }
      
      return { shooterTeamDelta: pointsMap[shooterPosition], opponentTeamDelta: 0, instantLoss: false }
    }

    return { shooterTeamDelta: 0, opponentTeamDelta: 0, instantLoss: false }
  }

  // Calculate points for a specific goal (used for score calculation)
  const calculateGoalPoints = (goal: GoalRecord): { redDelta: number; blueDelta: number; instantLoss: boolean; losingTeam?: Team } => {
    const { scoringTeam, shooterPosition, eventType, wasTouched, monsterblockTarget } = goal
    
    // Monsterblock: blocker team gets points
    if (eventType === 'monsterblock' && monsterblockTarget) {
      const points = wasTouched ? 1 : (monsterblockTarget === 'goalkeeper' ? 5 : 3)
      return scoringTeam === 'red'
        ? { redDelta: points, blueDelta: 0, instantLoss: false }
        : { redDelta: 0, blueDelta: points, instantLoss: false }
    }
    
    // Gamelle: opponent gets negative points
    if (eventType === 'gamelle') {
      const points = {
        'attack': wasTouched ? -1 : -2,
        'midfield': wasTouched ? -1 : -1,
        'defense': wasTouched ? -1 : -3,
        'goalkeeper': wasTouched ? -1 : -5
      }[shooterPosition]
      
      // Points go to OPPONENT team
      return scoringTeam === 'red'
        ? { redDelta: 0, blueDelta: points, instantLoss: false }
        : { redDelta: points, blueDelta: 0, instantLoss: false }
    }
    
    // Own Goal
    if (eventType === 'own_goal') {
      if (shooterPosition === 'attack') {
        // Own goal from attack: team that scored gets points (+2 if not touched, +1 for opponent if touched)
        if (!wasTouched) {
          // Not touched: scoring team gets +2
          return scoringTeam === 'red'
            ? { redDelta: 2, blueDelta: 0, instantLoss: false }
            : { redDelta: 0, blueDelta: 2, instantLoss: false }
        } else {
          // Touched: opponent gets +1
          return scoringTeam === 'red'
            ? { redDelta: 0, blueDelta: 1, instantLoss: false }
            : { redDelta: 1, blueDelta: 0, instantLoss: false }
        }
      } else {
        // Midfield/Defense/Goalkeeper own goal
        if (!wasTouched) {
          return { redDelta: 0, blueDelta: 0, instantLoss: true, losingTeam: scoringTeam }
        } else {
          // Touched: opponent gets 1 point
          return scoringTeam === 'red'
            ? { redDelta: 0, blueDelta: 1, instantLoss: false }
            : { redDelta: 1, blueDelta: 0, instantLoss: false }
        }
      }
    }
    
    // Normal Shot
    const points = {
      'attack': wasTouched ? 1 : 2,
      'midfield': wasTouched ? 1 : -1,
      'defense': wasTouched ? 1 : 3,
      'goalkeeper': wasTouched ? 1 : 5
    }[shooterPosition]
    
    return scoringTeam === 'red'
      ? { redDelta: points, blueDelta: 0, instantLoss: false }
      : { redDelta: 0, blueDelta: points, instantLoss: false }
  }

  // Calculate current scores
  const calculateCurrentScores = () => {
    let redScore = 0
    let blueScore = 0

    for (const goal of goals) {
      const result = calculateGoalPoints(goal)
      
      if (result.instantLoss) {
        return { redScore: 0, blueScore: 0, instantLoss: true, losingTeam: result.losingTeam }
      }

      redScore += result.redDelta
      blueScore += result.blueDelta
    }

    return { redScore, blueScore, instantLoss: false }
  }

  const scores = calculateCurrentScores()
  const redScore = scores.instantLoss ? 0 : scores.redScore
  const blueScore = scores.instantLoss ? 0 : scores.blueScore

  const addGoal = () => {
    // Validate monsterblock target is selected
    if (currentGoal.eventType === 'monsterblock' && !currentGoal.monsterblockTarget) {
      alert('Please select which position you blocked (Defense or Goalkeeper)')
      return
    }

    const newGoal: GoalRecord = {
      id: Date.now().toString(),
      scoringTeam: currentGoal.scoringTeam,
      shooterPosition: currentGoal.shooterPosition,
      eventType: currentGoal.eventType,
      wasTouched: currentGoal.wasTouched,
      points: 0, // Will be calculated below
      monsterblockTarget: currentGoal.monsterblockTarget,
      timestamp: new Date()
    }

    // Calculate the actual points using the new helper
    const goalPoints = calculateGoalPoints(newGoal)
    
    // For display: show the net effect of this goal
    // For most goals, this is the points the scoring team gets
    // For Gamelle/Monsterblock, need to show the actual effect
    if (currentGoal.eventType === 'gamelle') {
      // Gamelle: show negative points opponent receives
      newGoal.points = currentGoal.scoringTeam === 'red' ? goalPoints.blueDelta : goalPoints.redDelta
    } else if (currentGoal.eventType === 'monsterblock') {
      // Monsterblock: show positive points blocker receives
      newGoal.points = currentGoal.scoringTeam === 'red' ? goalPoints.redDelta : goalPoints.blueDelta
    } else {
      // Normal goals: show points for scoring team
      newGoal.points = currentGoal.scoringTeam === 'red' ? goalPoints.redDelta : goalPoints.blueDelta
    }

    setGoals([...goals, newGoal])

    // Check for instant loss
    if (goalPoints.instantLoss) {
      setInstantLoss({ 
        team: goalPoints.losingTeam!, 
        reason: `Own goal from ${currentGoal.shooterPosition}` 
      })
      setGameEnded(true)
    }

    // Check if game has reached target score
    const newScores = calculateCurrentScores()
    if (!newScores.instantLoss) {
      const newRed = newScores.redScore
      const newBlue = newScores.blueScore
      
      if (newRed >= targetScore) {
        setGameEnded(true)
      } else if (newBlue >= targetScore) {
        setGameEnded(true)
      } else if (newRed <= -10 || newBlue <= -10) {
        setGameEnded(true)
      }
    }
  }

  const removeGoal = (goalId: string) => {
    setGoals(goals.filter(g => g.id !== goalId))
    setGameEnded(false)
    setInstantLoss(null)
  }

  const undoLast = () => {
    if (goals.length > 0) {
      setGoals(goals.slice(0, -1))
      setGameEnded(false)
      setInstantLoss(null)
    }
  }

  const swapPositions = (team: Team) => {
    if (team === 'red') {
      const oldDefense = participants.redDefense
      const oldAttack = participants.redAttack
      
      // Swap the participants
      setParticipants({
        ...participants,
        redDefense: oldAttack,
        redAttack: oldDefense
      })
      
      // Record the swap with the TWO players involved (order doesn't matter for tracking)
      setPositionSwaps([...positionSwaps, {
        id: Date.now().toString(),
        team: 'red',
        timestamp: new Date(),
        defensePlayer: oldDefense || '',  // Player who WAS in defense
        attackPlayer: oldAttack || ''     // Player who WAS in attack
      }])
    } else {
      const oldDefense = participants.blueDefense
      const oldAttack = participants.blueAttack
      
      // Swap the participants
      setParticipants({
        ...participants,
        blueDefense: oldAttack,
        blueAttack: oldDefense
      })
      
      // Record the swap with the TWO players involved (order doesn't matter for tracking)
      setPositionSwaps([...positionSwaps, {
        id: Date.now().toString(),
        team: 'blue',
        timestamp: new Date(),
        defensePlayer: oldDefense || '',  // Player who WAS in defense
        attackPlayer: oldAttack || ''     // Player who WAS in attack
      }])
    }
  }

  const handleSubmit = () => {
    const participantsList: Array<{ user_id: string; team: Team; role: Role }> = []
    if (gameType === '2v2') {
      if (participants.redDefense) participantsList.push({ user_id: participants.redDefense, team: 'red', role: 'defense' })
      if (participants.redAttack) participantsList.push({ user_id: participants.redAttack, team: 'red', role: 'attack' })
      if (participants.blueDefense) participantsList.push({ user_id: participants.blueDefense, team: 'blue', role: 'defense' })
      if (participants.blueAttack) participantsList.push({ user_id: participants.blueAttack, team: 'blue', role: 'attack' })
    } else {
      if (participants.redDefense) participantsList.push({ user_id: participants.redDefense, team: 'red', role: 'full' })
      if (participants.blueDefense) participantsList.push({ user_id: participants.blueDefense, team: 'blue', role: 'full' })
    }

    // Map goals to backend format
    const goalsPayload = goals.map((goal) => {
      let goalType = ''
      
      // Determine goal_type based on event and position
      if (goal.eventType === 'shot') {
        goalType = `${goal.shooterPosition}_shot`
      } else if (goal.eventType === 'own_goal') {
        goalType = `own_goal_${goal.shooterPosition}`
      } else if (goal.eventType === 'monsterblock') {
        // For monsterblock, the goal_type is the position that was BLOCKED, not the blocker
        goalType = `monsterblock_${goal.monsterblockTarget}`
      } else if (goal.eventType === 'gamelle') {
        // For gamelle, include the position: gamelle_attack, gamelle_defense, etc.
        goalType = `gamelle_${goal.shooterPosition}`
      }

      return {
        scored_by_team: goal.scoringTeam,
        shooter_role: goal.shooterPosition,
        goal_type: goalType,
        touched_by_defender: goal.wasTouched,
        goal_time: goal.timestamp.toISOString(),
      }
    })

    // Map position swaps to backend format
    const positionSwapsPayload = positionSwaps.map((swap) => ({
      team: swap.team,
      player1_id: swap.defensePlayer,  // Player who WAS in defense before swap
      player2_id: swap.attackPlayer,   // Player who WAS in attack before swap
      swap_time: swap.timestamp.toISOString(),
    }))

    createGame.mutate({
      team_id: currentTeam?.id,
      game_type: gameType,
      game_mode: gameMode,
      target_score: targetScore,
      participants: participantsList,
      quick_entry: false,
      final_scores: {
        red: redScore,
        blue: blueScore,
      },
      goals: goalsPayload,
      position_swaps: positionSwapsPayload,
    })
  }

  if (usersLoading) return <LoadingSpinner size="lg" />
  const approvedUsers = users?.filter((u: any) => u.status === 'approved') || []

  // Get point preview for current selection
  const getPointPreview = (eventType: EventType, position: ShooterPosition, monsterblockTarget?: 'defense' | 'goalkeeper') => {
    const touched = currentGoal.wasTouched
    
    if (eventType === 'shot') {
      const points = {
        'attack': touched ? 1 : 2,
        'midfield': touched ? 1 : -1,
        'defense': touched ? 1 : 3,
        'goalkeeper': touched ? 1 : 5
      }[position]
      return { points, isNegative: points < 0 }
    }
    
    if (eventType === 'own_goal') {
      if (position === 'attack') {
        // Attack own goal: +2 for shooter team if not touched, +1 for opponent if touched
        return { 
          points: touched ? 1 : 2, 
          isNegative: false, 
          isOpponentPoints: touched,
          instantLoss: false
        }
      } else {
        // Midfield/Defense/Goalkeeper own goal
        return { 
          points: touched ? 1 : 0, 
          isNegative: false, 
          instantLoss: !touched, 
          isOpponentPoints: touched 
        }
      }
    }
    
    if (eventType === 'gamelle') {
      const points = {
        'attack': touched ? -1 : -2,
        'midfield': touched ? -1 : -1,  // Corrected: Gamelle from midfield is -1 for opponent
        'defense': touched ? -1 : -3,
        'goalkeeper': touched ? -1 : -5
      }[position]
      return { points, isNegative: points < 0 }
    }
    
    if (eventType === 'monsterblock' && monsterblockTarget) {
      const points = touched ? 1 : (monsterblockTarget === 'goalkeeper' ? 5 : 3)
      return { points, isNegative: false }
    }
    
    return { points: 0, isNegative: false }
  }

  const ToggleButton = ({ active, onClick, children, color = 'blue', warning = false, disabled = false }: { 
    active: boolean
    onClick: () => void
    children: React.ReactNode
    color?: 'blue' | 'red' | 'green' | 'yellow' | 'orange'
    warning?: boolean
    disabled?: boolean
  }) => {
    const colorClasses = {
      blue: active ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 text-gray-700 hover:bg-gray-50',
      red: active ? 'bg-red-600 border-red-600 text-white' : 'border-red-300 text-gray-700 hover:bg-red-50 border-2',
      green: active ? 'bg-green-600 border-green-600 text-white' : 'border-gray-300 text-gray-700 hover:bg-gray-50',
      yellow: active ? 'bg-yellow-500 border-yellow-500 text-white' : 'border-yellow-300 text-gray-700 hover:bg-yellow-50 border-2',
      orange: active ? 'bg-orange-600 border-orange-600 text-white' : 'border-gray-300 text-gray-700 hover:bg-gray-50',
    }
    
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${colorClasses[color]} ${
          warning && !active ? 'border-yellow-400 bg-yellow-50' : ''
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {children}
      </button>
    )
  }

  const getEventLabel = (event: EventType) => {
    return {
      shot: 'Shot',
      monsterblock: 'Monsterblock',
      gamelle: 'Gamelle',
      own_goal: 'Own Goal'
    }[event]
  }

  const getPositionLabel = (pos: ShooterPosition) => {
    return {
      attack: 'Attack',
      midfield: 'Midfield',
      defense: 'Defense',
      goalkeeper: 'Goalkeeper'
    }[pos]
  }

  return (
    <div className="space-y-6">
      {/* Player Selection */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Select Players</h3>
        
        {gameType === '2v2' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Red Team */}
            <div className="space-y-4">
              <h4 className="font-medium text-red-600">Red Team</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Defense</label>
                <select
                  value={participants.redDefense || ''}
                  onChange={(e) => setParticipants({ ...participants, redDefense: e.target.value })}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500"
                >
                  <option value="">Select player...</option>
                  {approvedUsers.map((user: any) => (
                    <option key={user.id} value={user.id}>{user.display_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Attack</label>
                <select
                  value={participants.redAttack || ''}
                  onChange={(e) => setParticipants({ ...participants, redAttack: e.target.value })}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500"
                >
                  <option value="">Select player...</option>
                  {approvedUsers.map((user: any) => (
                    <option key={user.id} value={user.id}>{user.display_name}</option>
                  ))}
                </select>
              </div>
              {/* Swap Button for Red Team */}
              {participants.redDefense && participants.redAttack && (
                <button
                  type="button"
                  onClick={() => swapPositions('red')}
                  className="w-full flex items-center justify-center px-4 py-2 bg-red-50 hover:bg-red-100 border border-red-300 rounded-md text-red-700 font-medium transition-colors"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Swap Positions
                </button>
              )}
            </div>

            {/* Blue Team */}
            <div className="space-y-4">
              <h4 className="font-medium text-blue-600">Blue Team</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Defense</label>
                <select
                  value={participants.blueDefense || ''}
                  onChange={(e) => setParticipants({ ...participants, blueDefense: e.target.value })}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="">Select player...</option>
                  {approvedUsers.map((user: any) => (
                    <option key={user.id} value={user.id}>{user.display_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Attack</label>
                <select
                  value={participants.blueAttack || ''}
                  onChange={(e) => setParticipants({ ...participants, blueAttack: e.target.value })}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="">Select player...</option>
                  {approvedUsers.map((user: any) => (
                    <option key={user.id} value={user.id}>{user.display_name}</option>
                  ))}
                </select>
              </div>
              {/* Swap Button for Blue Team */}
              {participants.blueDefense && participants.blueAttack && (
                <button
                  type="button"
                  onClick={() => swapPositions('blue')}
                  className="w-full flex items-center justify-center px-4 py-2 bg-blue-50 hover:bg-blue-100 border border-blue-300 rounded-md text-blue-700 font-medium transition-colors"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Swap Positions
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-red-600 mb-1">Red Player</label>
              <select
                value={participants.redDefense || ''}
                onChange={(e) => setParticipants({ ...participants, redDefense: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500"
              >
                <option value="">Select player...</option>
                {approvedUsers.map((user: any) => (
                  <option key={user.id} value={user.id}>{user.display_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-blue-600 mb-1">Blue Player</label>
              <select
                value={participants.blueDefense || ''}
                onChange={(e) => setParticipants({ ...participants, blueDefense: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">Select player...</option>
                {approvedUsers.map((user: any) => (
                  <option key={user.id} value={user.id}>{user.display_name}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Score Preview */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Current Score</h3>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Target className="h-4 w-4" />
            <span>Target: {targetScore}</span>
          </div>
        </div>
        
        <div className="flex items-center justify-center space-x-8">
          <div className="text-center">
            <div className="text-5xl font-bold text-red-600">{redScore}</div>
            <div className="text-sm font-medium text-gray-600 mt-2">Red</div>
            {redScore >= targetScore && !instantLoss && <Trophy className="h-6 w-6 mx-auto mt-2 text-yellow-500" />}
          </div>
          <div className="text-3xl font-light text-gray-400">-</div>
          <div className="text-center">
            <div className="text-5xl font-bold text-blue-600">{blueScore}</div>
            <div className="text-sm font-medium text-gray-600 mt-2">Blue</div>
            {blueScore >= targetScore && !instantLoss && <Trophy className="h-6 w-6 mx-auto mt-2 text-yellow-500" />}
          </div>
        </div>

        {instantLoss && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <p className="text-sm font-medium text-red-900">
                Instant Loss - {instantLoss.team === 'red' ? 'Red' : 'Blue'} Team Loses!
              </p>
            </div>
            <p className="text-xs text-red-700 mt-1">{instantLoss.reason}</p>
          </div>
        )}

        {(redScore >= targetScore || blueScore >= targetScore) && !instantLoss && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm font-medium text-green-900">
              {redScore >= targetScore ? 'Red' : 'Blue'} Team Wins!
            </p>
          </div>
        )}
      </div>

      {/* Add Goal Section */}
      {!gameEnded && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Record Goal</h3>
          
          <div className="space-y-4">
            {/* 1. Scoring Team */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">1. Scoring Team</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setCurrentGoal({ ...currentGoal, scoringTeam: 'red' })}
                  className={`flex flex-col items-center p-4 rounded-lg border-2 transition-all ${
                    currentGoal.scoringTeam === 'red'
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-200 bg-white hover:border-red-300'
                  }`}
                >
                  <div className="text-lg font-bold text-red-600 mb-2">RED</div>
                  <div className="flex flex-col items-center space-y-1.5 w-full">
                    {getTeamPlayers('red').map((player) => (
                      <div key={player.id} className="flex items-center space-x-2">
                        {player.profile_picture_url ? (
                          <img
                            src={player.profile_picture_url}
                            alt={player.display_name}
                            className="h-8 w-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center">
                            <User className="h-4 w-4 text-red-600" />
                          </div>
                        )}
                        <span className="text-sm font-medium text-gray-700 truncate max-w-[120px]">
                          {player.display_name}
                        </span>
                      </div>
                    ))}
                  </div>
                </button>
                <button
                  onClick={() => setCurrentGoal({ ...currentGoal, scoringTeam: 'blue' })}
                  className={`flex flex-col items-center p-4 rounded-lg border-2 transition-all ${
                    currentGoal.scoringTeam === 'blue'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-blue-300'
                  }`}
                >
                  <div className="text-lg font-bold text-blue-600 mb-2">BLUE</div>
                  <div className="flex flex-col items-center space-y-1.5 w-full">
                    {getTeamPlayers('blue').map((player) => (
                      <div key={player.id} className="flex items-center space-x-2">
                        {player.profile_picture_url ? (
                          <img
                            src={player.profile_picture_url}
                            alt={player.display_name}
                            className="h-8 w-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <User className="h-4 w-4 text-blue-600" />
                          </div>
                        )}
                        <span className="text-sm font-medium text-gray-700 truncate max-w-[120px]">
                          {player.display_name}
                        </span>
                      </div>
                    ))}
                  </div>
                </button>
              </div>
            </div>

            {/* 2. Shooter Position */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">2. Shooter Position</label>
              <div className="flex flex-wrap gap-2">
                <ToggleButton
                  active={currentGoal.shooterPosition === 'attack'}
                  onClick={() => setCurrentGoal({ ...currentGoal, shooterPosition: 'attack' })}
                >
                  Attack
                </ToggleButton>
                <ToggleButton
                  active={currentGoal.shooterPosition === 'midfield'}
                  onClick={() => setCurrentGoal({ ...currentGoal, shooterPosition: 'midfield' })}
                >
                  Midfield
                </ToggleButton>
                <ToggleButton
                  active={currentGoal.shooterPosition === 'defense'}
                  onClick={() => setCurrentGoal({ ...currentGoal, shooterPosition: 'defense' })}
                >
                  Defense
                </ToggleButton>
                <ToggleButton
                  active={currentGoal.shooterPosition === 'goalkeeper'}
                  onClick={() => setCurrentGoal({ ...currentGoal, shooterPosition: 'goalkeeper' })}
                >
                  Goalkeeper
                </ToggleButton>
              </div>
            </div>

            {/* 3. Event Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">3. Event Type</label>
              <div className="flex flex-wrap gap-2">
                {(() => {
                  const shotPreview = getPointPreview('shot', currentGoal.shooterPosition)
                  const gamellePreview = getPointPreview('gamelle', currentGoal.shooterPosition)
                  const ownGoalPreview = getPointPreview('own_goal', currentGoal.shooterPosition)
                  
                  return (
                    <>
                      <ToggleButton
                        active={currentGoal.eventType === 'shot'}
                        onClick={() => setCurrentGoal({ ...currentGoal, eventType: 'shot', monsterblockTarget: undefined })}
                        color="green"
                      >
                        Shot <span className={shotPreview.isNegative ? 'text-red-300' : 'text-green-300'}>
                          {shotPreview.points > 0 ? '+' : ''}{shotPreview.points}
                        </span>
                      </ToggleButton>
                      
                      {/* Monsterblock only available for Attack position */}
                      {currentGoal.shooterPosition === 'attack' && (
                        <ToggleButton
                          active={currentGoal.eventType === 'monsterblock'}
                          onClick={() => setCurrentGoal({ ...currentGoal, eventType: 'monsterblock' })}
                          color="blue"
                        >
                          Monsterblock +?
                        </ToggleButton>
                      )}
                      
                      <ToggleButton
                        active={currentGoal.eventType === 'gamelle'}
                        onClick={() => setCurrentGoal({ ...currentGoal, eventType: 'gamelle', monsterblockTarget: undefined })}
                        color="blue"
                      >
                        Gamelle <span className={gamellePreview.isNegative ? 'text-red-300' : 'text-green-300'}>
                          {gamellePreview.points > 0 ? '+' : ''}{gamellePreview.points}
                        </span>
                      </ToggleButton>
                      
                      <ToggleButton
                        active={currentGoal.eventType === 'own_goal'}
                        onClick={() => setCurrentGoal({ ...currentGoal, eventType: 'own_goal', monsterblockTarget: undefined })}
                        color={currentGoal.shooterPosition === 'attack' ? 'yellow' : 'red'}
                      >
                        Own Goal {currentGoal.shooterPosition !== 'attack' && '⚠️'} 
                        {ownGoalPreview.instantLoss ? (
                          <span className="text-yellow-300 ml-1">LOSS</span>
                        ) : (
                          <span className={ownGoalPreview.isNegative ? 'text-red-300' : 'text-green-300'}>
                            {ownGoalPreview.isOpponentPoints ? ' (Opp +' : ' +'}
                            {ownGoalPreview.points}
                            {ownGoalPreview.isOpponentPoints ? ')' : ''}
                          </span>
                        )}
                      </ToggleButton>
                    </>
                  )
                })()}
              </div>
              
              {/* Monsterblock Target Selection */}
              {currentGoal.eventType === 'monsterblock' && currentGoal.shooterPosition === 'attack' && (
                <div className="mt-3 pl-4 border-l-2 border-blue-300">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Which position did you block?
                  </label>
                  <div className="flex gap-2">
                    <ToggleButton
                      active={currentGoal.monsterblockTarget === 'defense'}
                      onClick={() => setCurrentGoal({ ...currentGoal, monsterblockTarget: 'defense' })}
                      color="green"
                    >
                      Defense <span className="text-green-300">+{currentGoal.wasTouched ? 1 : 3}</span>
                    </ToggleButton>
                    <ToggleButton
                      active={currentGoal.monsterblockTarget === 'goalkeeper'}
                      onClick={() => setCurrentGoal({ ...currentGoal, monsterblockTarget: 'goalkeeper' })}
                      color="green"
                    >
                      Goalkeeper <span className="text-green-300">+{currentGoal.wasTouched ? 1 : 5}</span>
                    </ToggleButton>
                  </div>
                </div>
              )}
              
              {/* Warning for dangerous own goal */}
              {currentGoal.eventType === 'own_goal' && currentGoal.shooterPosition !== 'attack' && !currentGoal.wasTouched && (
                <div className="mt-2 p-3 bg-red-50 border border-red-300 rounded-md">
                  <div className="flex items-start space-x-2">
                    <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-800">
                      <p className="font-medium">⚠️ INSTANT LOSS!</p>
                      <p className="text-xs mt-1">Untouched own goal from {getPositionLabel(currentGoal.shooterPosition)} causes instant loss for {currentGoal.scoringTeam === 'red' ? 'Red' : 'Blue'} team!</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 4. Ball Was Touched */}
            <div>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={currentGoal.wasTouched}
                  onChange={(e) => setCurrentGoal({ ...currentGoal, wasTouched: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Ball was touched</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Check if the ball was deflected by any figure after the original shot
              </p>
            </div>

            {/* Add Goal Button */}
            <div className="pt-2">
              <button
                onClick={addGoal}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
              >
                Add Goal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Goals and Events List */}
      {(goals.length > 0 || positionSwaps.length > 0) && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Game Events ({goals.length + positionSwaps.length})
            </h3>
            <button
              onClick={undoLast}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Undo Last Goal
            </button>
          </div>
          
          <div className="space-y-2">
            {/* Merge goals and swaps, sort by timestamp */}
            {[...goals.map(g => ({ ...g, type: 'goal' as const })), ...positionSwaps.map(s => ({ ...s, type: 'swap' as const }))]
              .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
              .map((event, index) => {
                if (event.type === 'swap') {
                  // Display position swap
                  const swap = event as PositionSwap & { type: 'swap' }
                  const defenseUser = users?.find((u: any) => u.id === swap.defensePlayer)
                  const attackUser = users?.find((u: any) => u.id === swap.attackPlayer)
                  
                  return (
                    <div
                      key={swap.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        swap.team === 'red' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <RefreshCw className={`h-5 w-5 ${swap.team === 'red' ? 'text-red-600' : 'text-blue-600'}`} />
                        <span className={`font-medium ${swap.team === 'red' ? 'text-red-700' : 'text-blue-700'}`}>
                          {swap.team === 'red' ? 'Red' : 'Blue'} Team Position Swap
                        </span>
                        <span className="text-gray-600">•</span>
                        <span className="text-sm text-gray-600">
                          {defenseUser?.display_name} ↔ {attackUser?.display_name}
                        </span>
                        <span className="text-gray-600">•</span>
                        <span className="text-xs text-gray-500">
                          {swap.timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  )
                }
                
                // Display goal
                const goal = event as GoalRecord & { type: 'goal' }
                const goalIndex = goals.findIndex(g => g.id === goal.id)
                // Construct goal_type to determine which team scored points
                let goalType = ''
                if (goal.eventType === 'shot') {
                  goalType = `${goal.shooterPosition}_shot`
                } else if (goal.eventType === 'own_goal') {
                  goalType = `own_goal_${goal.shooterPosition}`
                } else if (goal.eventType === 'monsterblock') {
                  goalType = `monsterblock_${goal.monsterblockTarget}`
                } else if (goal.eventType === 'gamelle') {
                  goalType = `gamelle_${goal.shooterPosition}`
                }
                
                // Determine which team benefited (scored positive points)
                // Use the points value to determine - negative points mean opposite team benefits
                const benefitingTeam = getTeamThatScoredPoints(
                  goal.scoringTeam, 
                  goalType, 
                  goal.wasTouched,
                  goal.points
                )
                
                return (
                <div
                  key={goal.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    benefitingTeam === 'red' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="font-medium text-gray-700">#{goalIndex + 1}</span>
                    <span className={`font-medium ${benefitingTeam === 'red' ? 'text-red-700' : 'text-blue-700'}`}>
                      {benefitingTeam === 'red' ? 'Red' : 'Blue'}
                    </span>
                    <span className="text-gray-600">•</span>
                    <span className="text-gray-700">{getPositionLabel(goal.shooterPosition)}</span>
                    <span className="text-gray-600">•</span>
                    <span className="text-gray-700">{getEventLabel(goal.eventType)}</span>
                    {goal.wasTouched && (
                      <>
                        <span className="text-gray-600">•</span>
                        <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">Touched</span>
                      </>
                    )}
                    <span className="text-gray-600">•</span>
                    <span className="text-xs text-gray-500">
                      {goal.timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`font-medium ${goal.points > 0 ? 'text-green-600' : goal.points < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                      {goal.points > 0 ? '+' : ''}{goal.points} pts
                    </span>
                    <button
                      onClick={() => removeGoal(goal.id)}
                      className="text-red-600 hover:text-red-800 transition-colors"
                      title="Delete this goal"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Submit Button */}
      {goals.length > 0 && (
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={createGame.isPending}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            {createGame.isPending ? 'Saving...' : 'Save Game'}
          </button>
        </div>
      )}
    </div>
  )
}

